import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CybersecurityServices = () => {
  // Hero Banner Section
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1920" 
          alt="Cybersecurity" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Comprehensive <span className="text-green-500">Cybersecurity Solutions</span>
        </h1>
        <p className="text-white text-xl max-w-3xl mx-auto">
          Protect your business from evolving cyber threats with our comprehensive security solutions, threat detection, and compliance management services.
        </p>
      </div>
    </section>
  );

  // Overview Section
  const OverviewSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=600&fit=crop" 
              alt="Cybersecurity" 
              className="rounded-lg shadow-2xl w-full"
            />
          </div>
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Safeguard Your Business with <span className="text-green-600">Advanced Security</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              In today's digital landscape, cybersecurity is not optional—it's essential. At Plexgate, we provide comprehensive security solutions to protect your business from cyber threats, data breaches, and compliance violations.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our team of certified security experts conducts thorough security audits, implements robust protection measures, and provides ongoing monitoring to ensure your systems remain secure. We help you build a security-first culture that protects your data, reputation, and bottom line.
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  // Services Section
  const ServicesSection = () => {
    const services = [
      {
        title: 'Security Audits & Assessments',
        description: 'Comprehensive security assessments, penetration testing, vulnerability scanning, and risk analysis to identify weaknesses.',
        icon: '🔍'
      },
      {
        title: 'Threat Detection & Response',
        description: '24/7 security monitoring, intrusion detection systems (IDS), security information and event management (SIEM), and incident response.',
        icon: '🛡️'
      },
      {
        title: 'Data Protection & Encryption',
        description: 'End-to-end encryption, data loss prevention (DLP), secure backup solutions, and database security.',
        icon: '🔐'
      },
      {
        title: 'Compliance Management',
        description: 'GDPR, HIPAA, PCI-DSS, SOC 2 compliance support, policy development, and regulatory audit preparation.',
        icon: '📋'
      },
      {
        title: 'Network Security',
        description: 'Firewall configuration, VPN setup, network segmentation, wireless security, and perimeter defense.',
        icon: '🌐'
      },
      {
        title: 'Security Training',
        description: 'Employee security awareness training, phishing simulations, security policy education, and best practices workshops.',
        icon: '🎓'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">Cybersecurity Services</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive protection across all layers of your IT infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Threat Protection Section
  const ThreatSection = () => {
    const threats = [
      { name: 'Ransomware', description: 'Malicious software that encrypts your data', color: 'red' },
      { name: 'Phishing', description: 'Fraudulent attempts to steal credentials', color: 'orange' },
      { name: 'DDoS Attacks', description: 'Overwhelming your systems with traffic', color: 'yellow' },
      { name: 'Insider Threats', description: 'Internal security risks from employees', color: 'purple' },
      { name: 'Zero-Day Exploits', description: 'Previously unknown vulnerabilities', color: 'blue' },
      { name: 'Data Breaches', description: 'Unauthorized access to sensitive data', color: 'pink' }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Protection Against <span className="text-green-600">Modern Threats</span>
            </h2>
            <p className="text-gray-600">We defend your business against the most common and dangerous cyber threats</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {threats.map((threat, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 border-l-4 border-red-500 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-2 text-gray-900">{threat.name}</h3>
                <p className="text-gray-600">{threat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Security Framework Section
  const FrameworkSection = () => {
    const steps = [
      {
        number: '01',
        title: 'Assessment',
        description: 'Comprehensive security audit to identify vulnerabilities, assess risks, and evaluate current security posture.'
      },
      {
        number: '02',
        title: 'Strategy',
        description: 'Develop customized security strategy aligned with your business goals and compliance requirements.'
      },
      {
        number: '03',
        title: 'Implementation',
        description: 'Deploy security solutions including firewalls, encryption, monitoring tools, and access controls.'
      },
      {
        number: '04',
        title: 'Monitoring',
        description: 'Continuous 24/7 monitoring, threat detection, incident response, and regular security updates.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our Security <span className="text-green-600">Framework</span>
            </h2>
            <p className="text-gray-600">A systematic approach to protecting your business</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-red-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Why Choose Section
  const WhyChooseSection = () => {
    const benefits = [
      {
        title: 'Certified Experts',
        description: 'Our team holds industry-recognized certifications including CISSP, CEH, CISM, and more.',
        icon: '🏆'
      },
      {
        title: '24/7 Monitoring',
        description: 'Round-the-clock security monitoring and rapid incident response to protect your business.',
        icon: '⏰'
      },
      {
        title: 'Compliance Support',
        description: 'Expert guidance on GDPR, HIPAA, PCI-DSS, and other regulatory compliance requirements.',
        icon: '✓'
      },
      {
        title: 'Proactive Defense',
        description: 'Advanced threat intelligence and proactive measures to prevent attacks before they happen.',
        icon: '🛡️'
      },
      {
        title: 'Regular Updates',
        description: 'Continuous security patches, updates, and vulnerability management to stay ahead of threats.',
        icon: '🔄'
      },
      {
        title: 'Cost-Effective',
        description: 'Comprehensive security solutions that fit your budget without compromising protection.',
        icon: '💰'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Why Trust <span className="text-green-600">Plexgate Security</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="text-4xl">{benefit.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };  // CTA Section
  const CTASection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Protect Your Business <span className="text-green-500">Today</span>
        </h2>
        <p className="text-white text-xl mb-8 max-w-3xl mx-auto">
          Don't wait for a security breach. Get a free security assessment and learn how we can protect your business.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Get Free Assessment →
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="cybersecurity-page">
      <HeroBannerSection />
      <OverviewSection />
      <ServicesSection />
      <ThreatSection />
      <FrameworkSection />
      <WhyChooseSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};



  // FAQ Section
  const FAQSection = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
    const faqs = [
      {
        question: 'How do I know if my business needs cybersecurity services?',
        answer: 'If you handle customer data, financial information, or rely on digital systems for operations, you need cybersecurity. Data breaches can cost businesses millions in damages, fines, and lost trust.'
      },
      {
        question: 'What is penetration testing?',
        answer: 'Penetration testing (pen testing) simulates real-world cyberattacks on your systems to identify vulnerabilities before malicious actors can exploit them. It\'s a proactive way to strengthen your security.'
      },
      {
        question: 'How much do cybersecurity services cost?',
        answer: 'Costs vary based on your business size, industry, and security requirements. We offer flexible packages from basic security audits to comprehensive managed security services. Contact us for a customized quote.'
      },
      {
        question: 'Do you help with compliance regulations?',
        answer: 'Yes! We provide compliance consulting for GDPR, HIPAA, PCI-DSS, SOC 2, and other regulations. We help you develop policies, implement controls, and prepare for audits.'
      },
      {
        question: 'What happens if we experience a security incident?',
        answer: 'Our incident response team is available 24/7 to contain threats, investigate breaches, recover systems, and prevent future incidents. We also provide forensic analysis and reporting.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-green-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg">{faq.question}</span>
                  <span className="text-2xl text-gray-600">
                    {expandedFAQ === index ? '−' : '+'}
                  </span>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };


export default CybersecurityServices;